import { t } from "i18next"
import { Checkbox, Modal, RadioButtons, SelectInput, Text, notification } from "components/common"
import {
  useLazyGetCurrentLeadSharingQuery,
  useShareLeadWithFractionalAutoMutation,
  useShareLeadWithFractionalManualMutation,
} from "features/clients/detail/api"
import { Form } from "antd"
import getPrivilege from "utils/privileges/privileges"
import getShareLeadFractionalValidationSchema from "./shareLeadFractionalValidationSchema"
import { schemaRules } from "features/shared/utils"
import { useLazyGetUsersQuery } from "features/shared/api"
import { useSelector } from "react-redux"
import { useMemo, useState } from "react"

const sharingTypes = () => [
  { value: "auto", label: t("Auto") },
  {
    value: "manual",
    label: t("Manual"),
  },
]

export default function ShareLeadFractionalForm({ leads, client, formRef, from = {}, onSuccess = () => {} }) {
  const privileges = useSelector(state => state.app.privileges)
  const [unShareOldUsers, setunShareOldUsers] = useState(false)
  const [form] = Form.useForm()
  const sharingType = Form.useWatch("sharingType", form)
  const selectUserToShareWith = Form.useWatch("user", form)
  const [
    shareLeadWithFractionalAuto,
    { isLoading: fetchingShareLeadFractionalAuto, error: errorShareLeadFractionalAuto, reset: autoTriggerReset },
  ] = useShareLeadWithFractionalAutoMutation()
  const [
    shareLeadWithFractionalManual,
    { isLoading: fetchingShareLeadFractionalManual, error: errorShareLeadFractionalManual, reset: manualTriggerReset },
  ] = useShareLeadWithFractionalManualMutation()

  const [getUsers, { data: usersListForFractionalSharing, isFetching: fetchingUsersListForFractionalSharing }] =
    useLazyGetUsersQuery()
  const [getCurrentUserSharing, { data: currentSharingData, isFetching: isFetchingCurrentSharing }] =
    useLazyGetCurrentLeadSharingQuery()
  const rules = schemaRules(getShareLeadFractionalValidationSchema())

  const fractionalSharingUsers = currentSharingData?.userList

  const onModalVisible = async () => {
    try {
      if (leads.length === 1) {
        const data = await getCurrentUserSharing(leads[0]?.id).unwrap()

        form.setFieldValue("user", data?.[leads[0]?.id]?.fractional?.[0]?.sharedWith?.id?.toString())
      }

      if (leads.length > 1) {
        await getCurrentUserSharing(leads.map(item => item?.id).toString()).unwrap()
      }

      await getUsers(
        {
          teamType: ["fractional_sales"],
          module: "leads",
          subModule: "shareWithFractional",
          allAgencyUsers: true,
        },
        true
      )
    } catch (error) {
      notification.error({ message: error?.data?.message || error?.message || error })
    }
  }

  const onSubmit = async values => {
    manualTriggerReset()
    autoTriggerReset()
    let user
    usersListForFractionalSharing?.some(e => {
      let users = e?.users?.find(it => +it?.id === +values?.user)
      if (users) {
        user = users
        return true
      }
    })
    const params = { ...values, leads, user }
    if (values?.sharingType === "auto") {
      const response = await shareLeadWithFractionalAuto(params).unwrap()
      
      if (response) {
        form.resetFields()
        formRef?.current?.setVisible(false)
        notification.success({ message: t("The lead has been shared successfully") })
        onSuccess(leads)
      }
    } else {
      const response = await shareLeadWithFractionalManual(params).unwrap()
      if (response) {
        form.resetFields()
        formRef.current.setVisible(false)
        notification.success({ message: t("The lead has been shared successfully") })
        onSuccess(leads)
      }
    }
  }

  const canUnShareWithFractional = useMemo(
    () => getPrivilege("can_stop_sharing_lead_with_fractional", privileges),
    [privileges]
  )

  return (
    <Modal
      title={t("Share Lead")}
      subTitle={t("Fractional")}
      ref={formRef}
      width={670}
      okText={t("Share Lead")}
      cancelText={t("Cancel")}
      loading={fetchingShareLeadFractionalManual || fetchingShareLeadFractionalAuto}
      onCancel={() => {
        formRef?.current?.setVisible(false)
        autoTriggerReset()
        manualTriggerReset()
        form.resetFields()
      }}
      onOk={form.submit}
      onModalVisible={onModalVisible}
      destroyOnClose
      footerAlert={errorShareLeadFractionalAuto || errorShareLeadFractionalManual}
      bodyStyle={{ overflowY: "visible" }}
      disableOkButton={
        fractionalSharingUsers?.length &&
        (sharingType === "auto"
          ? canUnShareWithFractional
            ? !unShareOldUsers
            : false
          : (canUnShareWithFractional ? !unShareOldUsers : false) ||
            fractionalSharingUsers?.includes(selectUserToShareWith))
      }
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          name="shareLeadFractionalForm"
          initialValues={{
            sharingType: "auto",
            user: null,
          }}
          onFinish={onSubmit}
          preserve={false}
        >
          <Form.Item name="sharingType" label={t("Sharing Type")} rules={rules}>
            <RadioButtons options={sharingTypes()} />
          </Form.Item>
          {sharingType === "manual" && (
            <Form.Item name="user" label={t("User")} rules={rules} preserve={true} required>
              <SelectInput
                placeholder={t("Select user")}
                options={usersListForFractionalSharing}
                loading={fetchingUsersListForFractionalSharing}
                onChange={value => {
                  form.setFieldsValue({ user: value })
                }}
              />
            </Form.Item>
          )}
          {!!fractionalSharingUsers?.length && canUnShareWithFractional && (
            <>
              <div>
                <Checkbox onChange={e => setunShareOldUsers(e.target.checked)}>
                  {t("Un-share with previous user and share again")}.
                </Checkbox>
              </div>
              <div>
                <Text type="danger">{t("The client is already shared with fraction.")}.</Text>
              </div>
            </>
          )}
        </Form>
      </div>
    </Modal>
  )
}